import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { AttendanceAdjustmentDialog } from "@/components/hr/AttendanceAdjustmentDialog";
import PermissionDenied from "@/components/PermissionDenied";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  attendanceApiErrorMessage,
  fetchAttendanceRecords,
  processAttendanceRecords,
  type AttendanceRecordRow,
  type PaginatedMeta,
} from "@/lib/hr/attendanceStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { format, isValid, parseISO } from "date-fns";
import { Check, ChevronLeft, ChevronRight, ChevronsUpDown, Loader2, Scale, User, Wand2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

function formatTimeFromApi(value: string | null): string {
  if (!value?.trim()) return "—";
  const normalized = value.trim().replace(" ", "T");
  const d = parseISO(normalized);
  if (!isValid(d)) {
    const parts = value.trim().split(" ");
    return parts.length > 1 ? parts[1] : value;
  }
  return format(d, "h:mm a").toLowerCase();
}

function weekdayName(dateIso: string): string {
  const d = parseISO(dateIso.slice(0, 10));
  if (!isValid(d)) return "—";
  return format(d, "EEEE");
}

function isActiveEmployee(employee: EmployeeRecord): boolean {
  const status = (employee.employeeStatus ?? "").trim();
  return status === "" || status === "Active";
}

function employeeDisplayName(employee: EmployeeRecord): string {
  return (employee.fullName || `${employee.firstName} ${employee.lastName}`).trim() || employee.id;
}

function employeeLabel(e: EmployeeRecord): string {
  const n = employeeDisplayName(e);
  return e.employeeCode ? `${e.employeeCode} · ${n}` : n;
}

function employeeInitials(e: EmployeeRecord): string {
  const first = (e.firstName || "").trim();
  const last = (e.lastName || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const full = (e.fullName || "").trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : full.slice(0, 2).toUpperCase();
  }
  return "??";
}

function employeeSubtitle(e: EmployeeRecord): string {
  const parts: string[] = [];
  if (e.departmentName?.trim()) parts.push(e.departmentName.trim());
  if (e.designationName?.trim()) parts.push(e.designationName.trim());
  return parts.join(" · ") || "";
}

function employeesMissingWorkPolicy(employees: EmployeeRecord[], scopedEmployeeId?: string): EmployeeRecord[] {
  const active = employees.filter(isActiveEmployee);
  const scoped = scopedEmployeeId ? active.filter((e) => e.id === scopedEmployeeId) : active;
  return scoped.filter((e) => !(e.workPolicyId ?? "").trim());
}

function workPolicyRequiredMessage(missing: EmployeeRecord[], scopedEmployeeId?: string): string {
  if (scopedEmployeeId) {
    return "Assign a work policy to this employee before processing attendance from punches.";
  }
  const preview = missing.slice(0, 5).map(employeeDisplayName).join(", ");
  const extra = missing.length > 5 ? ` (and ${missing.length - 5} more)` : "";
  return `Assign a work policy to all active employees before processing. Missing: ${preview}${extra}.`;
}

function statusClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "present") return "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300";
  if (s === "late") return "bg-orange-500/15 text-orange-800 dark:text-orange-300";
  if (s === "off_worked") return "bg-warning/15 text-warning";
  if (s === "absent") return "bg-destructive/15 text-destructive";
  if (s === "half_day") return "bg-amber-500/15 text-amber-900 dark:text-amber-300";
  if (s === "paid_leave") {
    return "bg-teal-600 text-white shadow-sm ring-1 ring-teal-700/40 dark:bg-teal-500 dark:text-teal-950 dark:ring-teal-300/50";
  }
  if (s === "unpaid_leave") {
    return "bg-rose-600 text-white shadow-sm ring-1 ring-rose-700/40 dark:bg-rose-500 dark:text-rose-950 dark:ring-rose-300/50";
  }
  if (s === "leave") return "bg-blue-500/15 text-blue-800 dark:text-blue-300";
  if (s === "holiday") return "bg-violet-500/15 text-violet-900 dark:text-violet-300";
  if (s === "off") return "bg-slate-600/30 text-slate-950 dark:bg-slate-500/35 dark:text-slate-100";
  return "bg-muted text-muted-foreground";
}

/** Full-row tint: Present stays default; other statuses get distinct backgrounds. */
function rowStatusClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "present") return "hover:bg-muted/20";
  if (s === "late") return "bg-orange-500/20 hover:bg-orange-500/25 dark:bg-orange-500/25 dark:hover:bg-orange-500/35 border-l-4 border-l-orange-500";
  if (s === "absent") return "bg-destructive/10 hover:bg-destructive/15";
  if (s === "off") return "bg-slate-600/28 hover:bg-slate-600/35 dark:bg-slate-500/35 dark:hover:bg-slate-500/45";
  if (s === "holiday") return "bg-violet-500/15 hover:bg-violet-500/20 dark:bg-violet-500/25 dark:hover:bg-violet-500/35";
  if (s === "off_worked") return "bg-warning/10 hover:bg-warning/15";
  if (s === "paid_leave") {
    return "bg-teal-500/20 hover:bg-teal-500/25 dark:bg-teal-500/25 dark:hover:bg-teal-500/35 border-l-4 border-l-teal-600";
  }
  if (s === "unpaid_leave") {
    return "bg-rose-500/20 hover:bg-rose-500/25 dark:bg-rose-500/25 dark:hover:bg-rose-500/35 border-l-4 border-l-rose-600";
  }
  if (s === "leave") return "bg-blue-500/10 hover:bg-blue-500/15";
  if (s === "half_day") return "bg-amber-500/10 hover:bg-amber-500/15";
  return "hover:bg-muted/20";
}

function statusLabel(status: string): string {
  const s = status.trim().toLowerCase();
  if (s === "off_worked") return "Off Day";
  if (s === "half_day") return "Half day";
  if (s === "holiday") return "Holiday";
  if (s === "paid_leave") return "Paid leave";
  if (s === "unpaid_leave") return "Unpaid leave";
  if (s === "leave") return "Leave";
  if (s === "late") return "Late";
  return s.replace(/_/g, " ");
}

/** Display status: present + late flag → "late" (stored status stays present). */
function displayStatus(status: string, isLate: boolean): string {
  if (isLate && status.trim().toLowerCase() === "present") {
    return "late";
  }
  return status;
}

function formatHoursMinutes(decimalHours: number): string {
  if (!Number.isFinite(decimalHours) || decimalHours <= 0) return "—";
  const totalMinutes = Math.round(decimalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function minutesAsHoursDisplay(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return "—";
  return formatHoursMinutes(minutes / 60);
}

/** Net adjustment minutes (can be negative); empty when zero to match other zero cells. */
function signedAdjustmentDisplay(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes === 0) return "—";
  const body = formatHoursMinutes(Math.abs(minutes) / 60);
  if (body === "—") return "—";
  return minutes < 0 ? `−${body}` : `+${body}`;
}

/** Punch OT on top; small signed adjustment underneath when present. */
function punchOtWithAdjBelow(punchMinutes: number, adjMinutes: number) {
  return (
    <div className="flex flex-col items-end leading-tight">
      <span>{punchMinutes > 0 ? minutesAsHoursDisplay(punchMinutes) : "—"}</span>
      {adjMinutes !== 0 ? (
        <span className="text-[10px] font-medium text-sky-700 dark:text-sky-300" title="Adjustment">
          {signedAdjustmentDisplay(adjMinutes)}
        </span>
      ) : null}
    </div>
  );
}

export type AttendanceDailyPanelProps = {
  /** Hide page-level title when embedded (e.g. dialog already has a title). */
  embedded?: boolean;
};

/**
 * Daily attendance UI (filters, Show, table).
 * Shared by the Daily Attendance page and Payroll Attendances create modal.
 */
export function AttendanceDailyPanel({ embedded = false }: AttendanceDailyPanelProps) {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canProcess =
    can(user, "attendance_daily_can_process_from_punches") ||
    can(user, "attendance_daily_can_process_current_month");
  const canAdjustCreate =
    can(user, "attendance_adjustment_can_create") || canProcess;
  const canAdjustView =
    can(user, "attendance_adjustment_can_view_any") ||
    can(user, "attendance_daily_can_view_any") ||
    canAdjustCreate;
  const [isForbidden, setIsForbidden] = useState(false);

  const [rows, setRows] = useState<AttendanceRecordRow[]>([]);
  const [meta, setMeta] = useState<PaginatedMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resultsReady, setResultsReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [employeeId, setEmployeeId] = useState<string>("");
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerCycle, setEmployeePickerCycle] = useState(0);
  const [adjustTarget, setAdjustTarget] = useState<AttendanceRecordRow | null>(null);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  const load = useCallback(
    async (pageOverride?: number) => {
      setLoading(true);
      setIsForbidden(false);
      const singleEmployee = Boolean(employeeId);
      const pageToUse = pageOverride ?? page;
      try {
        const { rows: data, meta: m } = await fetchAttendanceRecords({
          dateFrom,
          dateTo,
          employeeId: employeeId || undefined,
          page: singleEmployee ? 1 : pageToUse,
          perPage: singleEmployee ? 100 : 20,
        });
        setRows(data);
        setMeta(m);
        setResultsReady(true);
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
          setRows([]);
          setMeta(null);
          setResultsReady(false);
          return;
        }
        toast.error(attendanceApiErrorMessage(e));
        setRows([]);
        setMeta(null);
        setResultsReady(false);
      } finally {
        setLoading(false);
      }
    },
    [dateFrom, dateTo, employeeId, page],
  );

  const clearResults = useCallback(() => {
    setRows([]);
    setMeta(null);
    setResultsReady(false);
    setPage(1);
  }, []);

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  // Changing filters clears the table — data only appears after Process.
  useEffect(() => {
    clearResults();
  }, [dateFrom, dateTo, employeeId, clearResults]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const missingWorkPolicyEmployees = useMemo(
    () => employeesMissingWorkPolicy(employees, employeeId || undefined),
    [employees, employeeId],
  );

  const runProcess = async () => {
    if (missingWorkPolicyEmployees.length > 0) {
      toast.error(workPolicyRequiredMessage(missingWorkPolicyEmployees, employeeId || undefined));
      return;
    }
    setProcessing(true);
    try {
      const res = await processAttendanceRecords({
        dateFrom,
        dateTo,
        employeeId: employeeId || undefined,
        includeNonWorkingDays: true,
      });
      toast.success(res.message);
      setPage(1);
      await load(1);
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setProcessing(false);
    }
  };

  const idPrefix = embedded ? "payroll-att" : "att";
  const hasSplitOt = useMemo(() => rows.some((r) => r.enableSplitOt), [rows]);
  const tableColCount = (hasSplitOt ? 14 : 12) + (canAdjustView ? 1 : 0);

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Daily attendance access required"
        message="You don’t have permission to view daily attendance. Ask an administrator to grant attendance_daily_can_view_any."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          {!embedded ? (
            <>
              <h1 className="text-2xl font-display font-bold text-foreground">Daily attendance</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Set the range, then click <span className="font-medium text-foreground">Show</span> to load attendance
                for that period.
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Set the range, then click <span className="font-medium text-foreground">Show</span> to load attendance for
              that period.
            </p>
          )}
        </div>
      </div>

      <div className="glass-card p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
          <div className="space-y-2 lg:col-span-2">
            <Label htmlFor={`${idPrefix}-from`} className="text-xs text-muted-foreground">
              From
            </Label>
            <DatePartsPicker
              id={`${idPrefix}-from`}
              value={dateFrom}
              onChange={(iso) => {
                setPage(1);
                setDateFrom(iso);
              }}
              fromYear={1980}
              toYear={new Date().getFullYear() + 1}
              placeholder="Select start date"
              className="w-full"
            />
          </div>
          <div className="space-y-2 lg:col-span-2">
            <Label htmlFor={`${idPrefix}-to`} className="text-xs text-muted-foreground">
              To
            </Label>
            <DatePartsPicker
              id={`${idPrefix}-to`}
              value={dateTo}
              onChange={(iso) => {
                setPage(1);
                setDateTo(iso);
              }}
              fromYear={1980}
              toYear={new Date().getFullYear() + 1}
              placeholder="Select end date"
              className="w-full"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-5">
            <Label>Employee</Label>
            <Popover
              modal
              open={employeePickerOpen}
              onOpenChange={(next) => {
                setEmployeePickerOpen(next);
                if (next) setEmployeePickerCycle((c) => c + 1);
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={employeePickerOpen}
                  className={cn(
                    "glass-input h-9 w-full justify-between px-3 font-normal",
                    !employeeId && "text-muted-foreground",
                  )}
                >
                  {employeeId ? (
                    <span className="flex items-center gap-2 truncate">
                      {(() => {
                        const sel = employees.find((e) => e.id === employeeId);
                        if (!sel) return "Select employee…";
                        return (
                          <>
                            <Avatar className="h-5 w-5 text-[8px]">
                              {sel.photoDataUrl ? (
                                <AvatarImage src={sel.photoDataUrl} alt={employeeDisplayName(sel)} />
                              ) : null}
                              <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                                {employeeInitials(sel)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate">{employeeLabel(sel)}</span>
                          </>
                        );
                      })()}
                    </span>
                  ) : (
                    <span className="truncate text-left">All employees</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
                sideOffset={4}
              >
                <Command key={employeePickerCycle} shouldFilter>
                  <CommandInput placeholder="Search name, code or department…" className="h-9" />
                  <CommandList>
                    <CommandEmpty>No employee found.</CommandEmpty>
                    <CommandGroup>
                      {/* "All employees" option */}
                      <CommandItem
                        value="__all__ all employees"
                        onSelect={() => {
                          setPage(1);
                          setEmployeeId("");
                          setEmployeePickerOpen(false);
                        }}
                        className="gap-2"
                      >
                        <Check className={cn("h-3.5 w-3.5 shrink-0", !employeeId ? "opacity-100" : "opacity-0")} />
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>All employees</span>
                      </CommandItem>
                      {activeEmployees.map((e) => {
                        const label = employeeLabel(e);
                        const subtitle = employeeSubtitle(e);
                        const selected = employeeId === e.id;
                        // searchable value includes name, code, department & designation
                        const searchValue = [
                          label,
                          e.employeeCode,
                          e.departmentName,
                          e.designationName,
                          e.id,
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <CommandItem
                            key={e.id}
                            value={searchValue}
                            onSelect={() => {
                              setPage(1);
                              setEmployeeId(e.id);
                              setEmployeePickerOpen(false);
                            }}
                            className="gap-2"
                          >
                            <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                            <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                              {e.photoDataUrl ? (
                                <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                              ) : null}
                              <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                {employeeInitials(e)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col min-w-0">
                              <span className="truncate text-sm font-medium">{label}</span>
                              {subtitle && (
                                <span className="truncate text-[11px] opacity-60 leading-tight">
                                  {subtitle}
                                </span>
                              )}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          {canProcess ? (
            <div className="sm:col-span-2 lg:col-span-3">
              <Button
                type="button"
                variant="premium"
                className="w-full gap-2"
                onClick={() => void runProcess()}
                disabled={loading || processing}
                title="Show"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                Show
              </Button>
            </div>
          ) : null}
        </div>

        <div className="overflow-x-auto rounded-lg border border-border/50">
          <table className="w-full min-w-[56rem] text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/30 text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="text-left font-semibold py-1.5 px-2.5">Date</th>
                <th className="text-left font-semibold py-1.5 px-2.5">Day</th>
                <th className="text-left font-semibold py-1.5 px-2.5">Employee</th>
                <th className="text-left font-semibold py-1.5 px-2.5">Shift</th>
                <th className="text-left font-semibold py-1.5 px-2.5">In</th>
                <th className="text-left font-semibold py-1.5 px-2.5">Out</th>
                <th className="text-right font-semibold py-1.5 px-2.5">Hours</th>
                {hasSplitOt ? (
                  <>
                    <th className="text-right font-semibold py-1.5 px-2.5" title="Shift Start Overtime">SOT</th>
                    <th className="text-right font-semibold py-1.5 px-2.5" title="Shift End Overtime">EOT</th>
                    <th className="text-right font-semibold py-1.5 px-2.5" title="Shift Start OT Adjustment">SAOT</th>
                    <th className="text-right font-semibold py-1.5 px-2.5" title="Shift End OT Adjustment">EAOT</th>
                  </>
                ) : (
                  <>
                    <th className="text-right font-semibold py-1.5 px-2.5">OT</th>
                    <th className="text-right font-semibold py-1.5 px-2.5" title="OT Adjustment">OT Adj</th>
                  </>
                )}
                <th className="text-right font-semibold py-1.5 px-2.5">ST</th>
                <th className="text-right font-semibold py-1.5 px-2.5">Late</th>
                <th className="text-center font-semibold py-1.5 px-2.5">Status</th>
                {canAdjustView ? (
                  <th className="text-center font-semibold py-1.5 px-2.5 w-10" title="Attendance adjustment">
                    <span className="sr-only">Adjust</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} className="py-10 text-center text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin inline-block" />
                  </td>
                </tr>
              ) : !resultsReady ? (
                <tr>
                  <td colSpan={tableColCount} className="py-8 text-center text-muted-foreground">
                    Click <span className="font-medium text-foreground">Show</span> to load attendance for this range.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} className="py-8 text-center text-muted-foreground">
                    No attendance records for this range.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const shownStatus = displayStatus(r.status, r.isLate);
                  return (
                  <tr key={r.id} className={cn("border-b border-border/40 last:border-0", rowStatusClass(shownStatus))}>
                    <td className="py-1 px-2.5 tabular-nums text-foreground">{r.date}</td>
                    <td className="py-1 px-2.5 text-muted-foreground">{weekdayName(r.date)}</td>
                    <td className="py-1 px-2.5 font-medium text-foreground">{r.employeeName ?? "—"}</td>
                    <td className="py-1 px-2.5 text-muted-foreground">{r.workShiftName ?? "—"}</td>
                    <td className="py-1 px-2.5 tabular-nums text-muted-foreground">{formatTimeFromApi(r.checkIn)}</td>
                    <td className="py-1 px-2.5 tabular-nums text-muted-foreground">{formatTimeFromApi(r.checkOut)}</td>
                    <td className="py-1 px-2.5 text-right tabular-nums">
                      {formatHoursMinutes(parseFloat(r.workedHours))}
                    </td>
                    {hasSplitOt ? (
                      <>
                        <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                          <div>
                            <span>{r.shiftStartOvertimeMinutes > 0 ? minutesAsHoursDisplay(r.shiftStartOvertimeMinutes) : "—"}</span>
                            {r.rawShiftStartOvertimeMinutes != null && r.rawShiftStartOvertimeMinutes > r.shiftStartOvertimeMinutes ? (
                              <span className="line-through text-[10px] text-muted-foreground/70 block font-mono">
                                {minutesAsHoursDisplay(r.rawShiftStartOvertimeMinutes)}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                          <div>
                            <span>{r.shiftEndOvertimeMinutes > 0 ? minutesAsHoursDisplay(r.shiftEndOvertimeMinutes) : "—"}</span>
                            {r.rawShiftEndOvertimeMinutes != null && r.rawShiftEndOvertimeMinutes > r.shiftEndOvertimeMinutes ? (
                              <span className="line-through text-[10px] text-muted-foreground/70 block font-mono">
                                {minutesAsHoursDisplay(r.rawShiftEndOvertimeMinutes)}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                          {signedAdjustmentDisplay(r.adjustmentStartOtMinutes)}
                        </td>
                        <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                          {signedAdjustmentDisplay(r.adjustmentEndOtMinutes)}
                        </td>
                      </>
                    ) : (
                      <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                        <div>
                          {r.offDayOvertimeMinutes > 0 ? (
                            <span title="Off Day Overtime" className="text-amber-600 font-medium dark:text-amber-400">
                              {minutesAsHoursDisplay(r.offDayOvertimeMinutes)}
                            </span>
                          ) : (
                            <span>{minutesAsHoursDisplay(r.overtimeMinutes)}</span>
                          )}
                          {r.rawOvertimeMinutes != null && r.rawOvertimeMinutes > (r.offDayOvertimeMinutes || r.overtimeMinutes) ? (
                            <span className="line-through text-[10px] text-muted-foreground/70 block font-mono">
                              {minutesAsHoursDisplay(r.rawOvertimeMinutes)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    )}
                    <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                      {r.shortMinutes > 0 ? minutesAsHoursDisplay(r.shortMinutes) : "—"}
                    </td>
                    <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                      {r.isLate || r.lateMinutes > 0 ? minutesAsHoursDisplay(r.lateMinutes) : "—"}
                    </td>
                    <td className="py-1 px-2.5 text-center">
                      <span
                        className={cn(
                          "inline-flex items-center text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full",
                          statusClass(shownStatus),
                        )}
                      >
                        {statusLabel(shownStatus)}
                      </span>
                    </td>
                    {canAdjustView ? (
                      <td className="py-1 px-1.5 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          title="Attendance adjustment"
                          onClick={() => setAdjustTarget(r)}
                        >
                          <Scale className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                  );
                })
              )}
            </tbody>
            {rows.length > 0 && (() => {
              const totalOt = rows.reduce((sum, r) => sum + (r.overtimeMinutes || 0), 0);
              const totalOffDayOt = rows.reduce((sum, r) => sum + (r.offDayOvertimeMinutes || 0), 0);
              const totalSt = rows.reduce((sum, r) => sum + (r.shortMinutes || 0), 0);
              const totalLate = rows.reduce((sum, r) => sum + (r.lateMinutes || 0), 0);
              const totalSot = rows.reduce((sum, r) => sum + (r.shiftStartOvertimeMinutes || 0), 0);
              const totalEot = rows.reduce((sum, r) => sum + (r.shiftEndOvertimeMinutes || 0), 0);
              const totalSaot = rows.reduce((sum, r) => sum + (r.adjustmentStartOtMinutes || 0), 0);
              const totalEaot = rows.reduce((sum, r) => sum + (r.adjustmentEndOtMinutes || 0), 0);
              const totalOtAdj = rows.reduce((sum, r) => sum + (r.adjustmentMinutes || 0), 0);

              return (
                <tfoot>
                  <tr className="border-t-2 border-border bg-muted/40 font-semibold text-sm">
                    <td colSpan={6} className="py-2 px-2.5 text-right uppercase tracking-wide text-muted-foreground text-xs">
                      Totals
                    </td>
                    <td className="py-2 px-2.5 text-right tabular-nums">
                      {formatHoursMinutes(
                        rows.reduce((sum, r) => sum + (parseFloat(r.workedHours) || 0), 0)
                      )}
                    </td>
                    {hasSplitOt ? (
                      <>
                        <td className="py-2 px-2.5 text-right tabular-nums">
                          {minutesAsHoursDisplay(totalSot)}
                        </td>
                        <td className="py-2 px-2.5 text-right tabular-nums">
                          {minutesAsHoursDisplay(totalEot)}
                        </td>
                        <td className="py-2 px-2.5 text-right tabular-nums">
                          {signedAdjustmentDisplay(totalSaot)}
                        </td>
                        <td className="py-2 px-2.5 text-right tabular-nums">
                          {signedAdjustmentDisplay(totalEaot)}
                        </td>
                      </>
                    ) : (
                      <td className="py-2 px-2.5 text-right tabular-nums">
                        <div className="flex flex-col items-end leading-tight text-xs">
                          <span>{minutesAsHoursDisplay(totalOt)}</span>
                          {totalOffDayOt > 0 && (
                            <span title="Off Day Overtime Total" className="text-amber-600 font-medium dark:text-amber-400">
                              {minutesAsHoursDisplay(totalOffDayOt)} <span className="text-[10px] font-normal text-muted-foreground">OD</span>
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="py-2 px-2.5 text-right tabular-nums">
                      {minutesAsHoursDisplay(totalSt)}
                    </td>
                    <td className="py-2 px-2.5 text-right tabular-nums">
                      {minutesAsHoursDisplay(totalLate)}
                    </td>
                    <td></td>
                    {canAdjustView ? <td></td> : null}
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>

        {!employeeId && resultsReady && meta && meta.lastPage > 1 ? (
          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>
              Page {meta.currentPage} of {meta.lastPage} ({meta.total} total)
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={meta.currentPage <= 1 || loading}
                onClick={() => {
                  const next = Math.max(1, page - 1);
                  setPage(next);
                  void load(next);
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={meta.currentPage >= meta.lastPage || loading}
                onClick={() => {
                  const next = page + 1;
                  setPage(next);
                  void load(next);
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {adjustTarget ? (
        <AttendanceAdjustmentDialog
          open={adjustTarget != null}
          onOpenChange={(open) => {
            if (!open) setAdjustTarget(null);
          }}
          employeeId={adjustTarget.employeeId}
          employeeName={adjustTarget.employeeName}
          adjustmentDate={adjustTarget.date}
          enableSplitOt={Boolean(adjustTarget.enableSplitOt)}
          canCreate={canAdjustCreate}
          onChanged={() => void load(page)}
        />
      ) : null}
    </div>
  );
}
